import { useEffect, useState } from 'react';
import { TrendingUp, BarChart2, Target, Shield } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import StatsCard from '../../../components/common/StatsCard';
import InfoCard from '../../../components/common/InfoCard';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import FundPerformanceChart from '../components/dashboard/FundPerformanceChart';
import { pammFundService } from '../services/pammService';
import { formatPercent, formatCurrency, getProfitColor } from '../utils';
import type { FundPerformance } from '../types';

export default function Performance() {
  const [perf, setPerf] = useState<FundPerformance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pammFundService.getPerformance('fund-001');
      setPerf(data);
    } catch {
      setError('Failed to load performance data.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, []);

  if (loading)
    return (
      <div className="space-y-4">
        <PageHeader title="Performance" />
        <LoadingSkeleton type="cards" cols={4} />
      </div>
    );
  if (error || !perf)
    return (
      <div className="space-y-4">
        <PageHeader title="Performance" />
        <PAMMErrorState message={error ?? 'No data.'} onRetry={load} />
      </div>
    );

  const s = perf.summary;

  return (
    <div className="space-y-6 pb-6">
      <PageHeader
        title="Performance"
        description="Comprehensive performance analytics across all PAMM funds"
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Return"
          value={formatPercent(s.totalReturn)}
          icon={TrendingUp}
        />
        <StatsCard
          title="Max Drawdown"
          value={formatPercent(-s.maxDrawdown)}
          icon={Shield}
        />
        <StatsCard
          title="Sharpe Ratio"
          value={s.sharpeRatio.toFixed(2)}
          icon={BarChart2}
        />
        <StatsCard title="Win Rate" value={`${s.winRate}%`} icon={Target} />
      </div>

      {/* Chart */}
      <FundPerformanceChart
        data={perf.data}
        title="Alpha Growth Fund — NAV & Returns"
      />

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InfoCard
          title="Risk Metrics"
          columns={2}
          fields={[
            {
              label: 'Max Drawdown',
              value: (
                <span className="text-red-500">
                  {formatPercent(-s.maxDrawdown)}
                </span>
              ),
            },
            { label: 'Sharpe Ratio', value: s.sharpeRatio.toFixed(2) },
            { label: 'Sortino Ratio', value: s.sortinoRatio.toFixed(2) },
            { label: 'Profit Factor', value: s.profitFactor.toFixed(2) },
          ]}
        />
        <InfoCard
          title="Trade Statistics"
          columns={2}
          fields={[
            { label: 'Total Trades', value: s.totalTrades.toLocaleString() },
            { label: 'Win Rate', value: `${s.winRate}%` },
            {
              label: 'Average Win',
              value: (
                <span className="text-green-600">
                  {formatCurrency(s.averageWin)}
                </span>
              ),
            },
            {
              label: 'Average Loss',
              value: (
                <span className="text-red-500">
                  {formatCurrency(s.averageLoss)}
                </span>
              ),
            },
          ]}
        />
      </div>

      {/* Monthly Data Table */}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                Month
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">
                NAV
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">
                Return %
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">
                Drawdown %
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground hidden md:table-cell">
                Volume
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground hidden md:table-cell">
                Trades
              </th>
            </tr>
          </thead>
          <tbody>
            {perf.data.map((row, i) => (
              <tr
                key={i}
                className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
              >
                <td className="px-4 py-2.5 text-xs font-medium">{row.date}</td>
                <td className="px-4 py-2.5 text-xs text-right font-mono">
                  {row.nav.toFixed(2)}
                </td>
                <td
                  className={`px-4 py-2.5 text-xs text-right font-semibold ${getProfitColor(row.return)}`}
                >
                  {formatPercent(row.return)}
                </td>
                <td className="px-4 py-2.5 text-xs text-right text-red-500">
                  {formatPercent(-row.drawdown)}
                </td>
                <td className="px-4 py-2.5 text-xs text-right text-muted-foreground hidden md:table-cell">
                  {row.volume.toLocaleString()}
                </td>
                <td className="px-4 py-2.5 text-xs text-right text-muted-foreground hidden md:table-cell">
                  {row.trades}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
